import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { IDossieCompanyInfo, IDossieFilters } from './dossieTypes';

/**
 * Carrega uma imagem e converte para base64
 */
const loadImageAsBase64 = (src: string): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      } else {
        reject(new Error('Erro ao criar contexto do canvas'));
      }
    };
    img.onerror = () => reject(new Error('Erro ao carregar imagem'));
    img.src = src;
  });
};

/**
 * Exporta o dashboard do dossiê para PDF usando html2canvas.
 * Captura o elemento HTML renderizado e converte para PDF com suporte a múltiplas páginas.
 *
 * @param elementId - ID do elemento HTML a ser capturado
 * @param companyInfo - Informações da empresa para nome do arquivo
 * @param filters - Filtros aplicados para nome do arquivo
 */
export const exportDossieToPDF = async (
  elementId: string,
  companyInfo: IDossieCompanyInfo,
  filters: IDossieFilters,
): Promise<void> => {
  const element = document.getElementById(elementId);
  if (!element) {
    throw new Error('Elemento não encontrado para exportação');
  }

  // Carregar logo
  let logoBase64: string | null = null;
  try {
    logoBase64 = await loadImageAsBase64('/logoBranca.png');
  } catch {
    // Se não conseguir carregar a logo, continua sem ela
    logoBase64 = null;
  }

  // Captura o elemento como canvas
  const canvas = await html2canvas(element, {
    scale: 1.5, // Boa qualidade com tamanho reduzido
    useCORS: true, // Permitir imagens de outros domínios
    logging: false, // Desabilitar logs
    backgroundColor: '#ffffff', // Fundo branco
    windowWidth: element.scrollWidth,
    windowHeight: element.scrollHeight,
  });

  const imgData = canvas.toDataURL('image/jpeg', 0.85); // JPEG com 85% qualidade

  // Configuração do PDF A4 em portrait com compressão
  // eslint-disable-next-line new-cap
  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
    compress: true, // Ativa compressão interna do PDF
  });

  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 10; // Margem em mm

  // Dimensões úteis da página (descontando margens)
  const usableWidth = pageWidth - margin * 2;
  const usableHeight = pageHeight - margin * 2;

  // Calcular dimensões da imagem mantendo proporção
  const imgWidth = usableWidth;
  const imgHeight = (canvas.height * imgWidth) / canvas.width;

  let heightLeft = imgHeight;
  let position = margin; // Posição Y inicial com margem

  // Função para formatar CNPJ
  const formatCNPJ = (cnpj: string): string => {
    const digits = cnpj.replace(/\D/g, '');
    return digits.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
  };

  // Adicionar cabeçalho na primeira página
  const headerHeight = 13;
  pdf.setFillColor(0, 130, 196); // #0082C4 - cor da marca
  pdf.rect(0, 0, pageWidth, headerHeight, 'F');

  // Adicionar logo no cabeçalho (se carregou)
  const logoSize = 20;
  const logoX = margin;
  const logoY = (headerHeight - logoSize) / 2;
  if (logoBase64) {
    pdf.addImage(logoBase64, 'PNG', logoX, logoY, logoSize, logoSize);
  }

  // Posição X para textos (após a logo)
  const textStartX = logoBase64 ? logoX + logoSize + 6 : margin;

  // Informações centralizadas verticalmente
  const centerY = headerHeight / 2;

  // Título da empresa (centralizado)
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(8);
  pdf.setFont('helvetica', 'bold');
  const nomeEmpresa = companyInfo.razao_social || companyInfo.nome || 'Empresa';
  pdf.text(nomeEmpresa, textStartX, centerY - 1);

  // CNPJ (centralizado)
  pdf.setFontSize(6);
  pdf.setFont('helvetica', 'normal');
  const cnpjFormatado = `CNPJ: ${formatCNPJ(companyInfo.cnpj)}`;
  pdf.text(cnpjFormatado, textStartX, centerY + 3);

  // Data de geração (canto direito, centralizado verticalmente)
  pdf.setFontSize(6);
  const dataGeracao = `Gerado em ${new Date().toLocaleDateString('pt-BR')}`;
  pdf.text(dataGeracao, pageWidth - margin - pdf.getTextWidth(dataGeracao), centerY + 1);

  // Ajustar posição inicial após o header
  const contentStartY = headerHeight + 5;
  position = contentStartY;

  // Altura disponível na primeira página (menor por causa do header)
  const firstPageUsableHeight = pageHeight - contentStartY - margin;

  // Primeira página
  if (imgHeight <= firstPageUsableHeight) {
    // Cabe em uma página
    pdf.addImage(imgData, 'JPEG', margin, position, imgWidth, imgHeight);
  } else {
    // Múltiplas páginas necessárias
    // Calcular quantos pixels da imagem cabem na primeira página
    const firstPagePixels = (firstPageUsableHeight / imgHeight) * canvas.height;

    // Criar canvas temporário para a primeira parte
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = canvas.width;
    tempCanvas.height = firstPagePixels;
    const tempCtx = tempCanvas.getContext('2d');
    if (tempCtx) {
      tempCtx.drawImage(
        canvas,
        0,
        0,
        canvas.width,
        firstPagePixels,
        0,
        0,
        canvas.width,
        firstPagePixels,
      );
      const firstPageImg = tempCanvas.toDataURL('image/jpeg', 0.85);
      pdf.addImage(firstPageImg, 'JPEG', margin, position, imgWidth, firstPageUsableHeight);
    }

    heightLeft = imgHeight - firstPageUsableHeight;
    let sourceY = firstPagePixels;

    // Páginas subsequentes
    while (heightLeft > 0) {
      pdf.addPage();

      // Calcular altura da próxima parte
      const remainingPixels = canvas.height - sourceY;
      const partPixels = Math.min(remainingPixels, (usableHeight / imgHeight) * canvas.height);
      const partHeight = Math.min(heightLeft, usableHeight);

      // Criar canvas temporário para esta parte
      const partCanvas = document.createElement('canvas');
      partCanvas.width = canvas.width;
      partCanvas.height = partPixels;
      const partCtx = partCanvas.getContext('2d');
      if (partCtx) {
        partCtx.drawImage(
          canvas,
          0,
          sourceY,
          canvas.width,
          partPixels,
          0,
          0,
          canvas.width,
          partPixels,
        );
        const partImg = partCanvas.toDataURL('image/jpeg', 0.85);
        pdf.addImage(partImg, 'JPEG', margin, margin, imgWidth, partHeight);
      }

      sourceY += partPixels;
      heightLeft -= partHeight;
    }
  }

  // Gerar nome do arquivo
  const cnpjLimpo = companyInfo.cnpj.replace(/\D/g, '');
  const filename = `dossie-${cnpjLimpo}-${filters.date_start}.pdf`;

  // Download
  pdf.save(filename);
};
